import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';
import { useTimezone } from '../context/TimezoneContext';

const Container = styled.div`
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  display: block;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-weight: ${props => props.theme.typography.weights.semiBold};
`;

const SelectWrapper = styled.div`
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.typography.sizes.default};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  transition: all 0.2s ease;
  min-height: 40px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &:disabled {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-top: none;
  border-radius: 0 0 ${props => props.theme.borderRadius.md} ${props => props.theme.borderRadius.md};
  z-index: 1000;
  box-shadow: ${props => props.theme.shadows.md};
`;

const Option = styled.div`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }

  ${props => props.selected && `
    background-color: ${props.theme.colors.tint}15;
    font-weight: ${props.theme.typography.weights.semiBold};
  `}
`;

const OptionText = styled.span`
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const OptionOffset = styled.span`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  margin-left: ${props => props.theme.spacing.sm};
  flex-shrink: 0;
`;

const BrowserDefaultOption = styled(Option)`
  border-bottom: 1px solid ${props => props.theme.colors.border};
  font-style: italic;
`;

const NoResults = styled.div`
  padding: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.muted};
  text-align: center;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const CurrentSelection = styled.div`
  margin-top: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

/**
 * Timezone Picker Component
 * Allows users to select their preferred timezone from a searchable dropdown
 */
export const TimezonePicker = ({
  label = 'Timezone',
  value,
  onChange,
  disabled = false,
  placeholder = 'Search timezones...',
  className,
}) => {
  const theme = useTheme();
  const { timezones, loadingTimezones, browserTimezone } = useTimezone();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter timezones based on search term
  const filteredTimezones = useMemo(() => {
    if (!searchTerm.trim()) return timezones;

    const term = searchTerm.toLowerCase();
    return timezones.filter(tz =>
      tz.id.toLowerCase().includes(term) ||
      tz.displayName.toLowerCase().includes(term) ||
      tz.utcOffset.toLowerCase().includes(term)
    );
  }, [timezones, searchTerm]);

  // Get display text for current selection
  const getDisplayText = () => {
    if (!value) return 'Use browser default';
    const selected = timezones.find(tz => tz.id === value);
    return selected ? selected.displayName : value;
  };

  const handleSelect = (timezoneId) => {
    onChange(timezoneId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = (e) => {
    // Delay closing to allow click on dropdown options
    setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
    }, 200);
  };

  return (
    <Container theme={theme} className={className}>
      {label && <Label theme={theme}>{label}</Label>}
      <SelectWrapper>
        <SearchInput
          theme={theme}
          type="text"
          value={isOpen ? searchTerm : getDisplayText()}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={isOpen ? placeholder : getDisplayText()}
          disabled={disabled || loadingTimezones}
        />
        {isOpen && (
          <Dropdown theme={theme}>
            {/* Browser Default Option */}
            <BrowserDefaultOption
              theme={theme}
              selected={!value}
              onClick={() => handleSelect(null)}
            >
              <OptionText theme={theme}>Use browser default</OptionText>
              <OptionOffset theme={theme}>({browserTimezone})</OptionOffset>
            </BrowserDefaultOption>

            {/* Filtered Timezones */}
            {filteredTimezones.length > 0 ? (
              filteredTimezones.map((tz) => (
                <Option
                  key={tz.id}
                  theme={theme}
                  selected={value === tz.id}
                  onClick={() => handleSelect(tz.id)}
                >
                  <OptionText theme={theme}>{tz.displayName}</OptionText>
                  <OptionOffset theme={theme}>{tz.utcOffset}</OptionOffset>
                </Option>
              ))
            ) : (
              <NoResults theme={theme}>
                No timezones found matching "{searchTerm}"
              </NoResults>
            )}
          </Dropdown>
        )}
      </SelectWrapper>
      <CurrentSelection theme={theme}>
        All times will be displayed in: {value || browserTimezone}
      </CurrentSelection>
    </Container>
  );
};

export default TimezonePicker;
